import { Component, Input, OnInit } from '@angular/core';
import { UIRouter }                        from '@uirouter/core';
import { TranslateService }               from '@ngx-translate/core';

import { ApiService }          from '../api/api.service';
import { CompanyCountryValidationsModel } from '../companies/models/company-country-validations';
import { CompanyMessagesModel } from '../companies/models/company.messages';
import { CompanyService }      from '../companies/company.service';
import { ConfirmService }      from '../messages/confirm/confirm.service';
import { CurrencyModel }      from '../core/models/currency';
import { MessagesService }     from '../messages/messages.service';
import { SelectOption }        from '../core/models/select-option';
import { SessionModel }        from '../sessions/models/session';
import { SessionService }      from '../sessions/session.service';
import { StreamSearchService } from '../api/stream-search.service';
import { VendorFactoryService } from './vendor-factory.service';
import { VendorIntegrationsService } from './vendor-integrations.service';
import { VendorModel }         from './models/vendor';

import VendorSearchResultsHtml from './templates/vendors.search.results.html';

@Component({
  selector   : 'wor-vendor',
  template: require('./vendor.component.html')
})
export class VendorComponent implements OnInit {
  @Input() billedVendors: VendorModel[];
  @Input() companyMessages: CompanyMessagesModel;
  @Input() invoices: Record<string, unknown>[];
  @Input() orders: Record<string, unknown>[];
  @Input() payments: Record<string, unknown>[];
  @Input() paymentTerms: SelectOption[];
  @Input() vendor: VendorModel;

  billerSearch: any;
  addressSearch: any;
  countryTypes: SelectOption[];
  loading = false;
  states: SelectOption[];
  validations: CompanyCountryValidationsModel;
  vendorObj: any;

  constructor (
    public  integrationsService: VendorIntegrationsService,
    private apiService: ApiService,
    public companyService: CompanyService,
    private confirmService: ConfirmService,
    private messagesService: MessagesService,
    private router: UIRouter,
    public  sessionService: SessionService,
    private streamSearchService: StreamSearchService,
    private vendorFactoryService: VendorFactoryService,
    private translateService: TranslateService
  ) {}

  get session () : SessionModel { return this.sessionService.base(); }
  get currency () : CurrencyModel { return this.sessionService.currency(); }

  checkRights (module: string): boolean {


    return this.sessionService.hasRights(module);


  }

  ngOnInit () {
    this.vendorObj = this.vendorFactoryService.init(this.vendor);

    this.addressSearch = {
      list: [],
      selected: { full_address: '', id: '' },
      search: (typed) => this.apiService.get('/NzPostal/get_addresses', typed)
        .then(addresses => addresses !== 'null' ? addresses : ''),
      choose: (address) => {
        this.loading = true;
        return this.apiService.get('/NzPostal/get_address_details', address.address_id)
        .then(details => {
          this.vendor.address1 = details.address1;
          this.vendor.address2 = details.address2;
          this.vendor.country_code = details.country_code;
          this.vendor.latitude = details.latitude;
          this.vendor.longitude = details.longitude;
          this.vendor.postcode = details.postcode;
          this.vendor.state = details.state;
          this.vendor.suburb = details.suburb;
        }).finally(() => this.loading = false);
      },
      format: () => typeof this.vendor.address1 === 'string' ? this.vendor.address1 : ''
    };

    this.billerSearch = {
      list: [],
      selected: null as any,
      templateUrl: VendorSearchResultsHtml,
      search: (typed) => this.streamSearchService.search('vendor_billers', typed)
        .then(vendors => this.billerSearch.list.concat(vendors)),
      choose: (vendor) => {
        this.vendor.biller_id = vendor.id;
        this.vendor.biller_name = vendor.company_name;
      },
      format: (vendor) => vendor ? vendor.company_name : ''
    };

    this.integrationsService.init({
      vendor: this.vendor,
      companyMessages: this.companyMessages,
      isBranchVendor: this.vendorObj.isBranchVendor()
    });

    if (this.vendor.country_code) {
      this.setCountryDefaults();
    } else {
      this.apiService.get('/system/state_types/', null, 'states').then(states => {
        this.states = states;
        this.validations = this.sessionService.countryValidation();
      });
    }

    this.apiService.get('/system/country_types/', null, 'country_types').then(types => this.countryTypes = types);

    if (this.vendor.biller_id) {


      this.getBiller();


      };


    }

  autolink () {
    this.apiService.get('/vendors/autolink_to_branch', this.vendor.id)
    .then(count => this.messagesService.show(`Autolink Created! ${count} Record(s) Linked.`, 'success'))
    .catch(err => this.messagesService.error(err));
  }

  delete () {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_SUPPLIER'))
    .then(() => this.apiService.delete('/vendor', this.vendor.id))
    .then(() => {
      this.router.stateService.go('app.vendors');
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.DELETED'), 'success');
    })
    .catch(err => this.messagesService.error(err));
  }

  emailStatement () {


    this.vendorObj.emailStatement?.()
    ?.then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.EMAIL_SENT'), 'success'));


  }

  getBiller () {
    this.apiService.get('/vendor', this.vendor.biller_id).then(biller => {
      this.billerSearch.selected = biller;
      this.vendor.biller_name = biller.company_name;
    }).catch(err => this.messagesService.error(err));
  }

  manuallyLinkVendor () {
    return this.vendorObj.manuallyLinkVendor()
    .then(() => {
      this.messagesService.show('Vendor Linked', 'success');
      this.router.stateService.reload();
    });
  }

  save () {
    if (!this.billerSearch.selected) {

      this.vendor.biller_id = null;

    }

    this.vendorObj.save().then(vendor => {
      this.vendor = vendor;
      this.vendorObj = this.vendorFactoryService.init(this.vendor);

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.SUPPLIER'), 'success');

      this.vendor.id
        ? this.router.stateService.reload()
        : this.router.stateService.go('app.vendor', { id: vendor.id });
    }).catch(err => this.messagesService.error(err));
  }

  setCountryDefaults () {
    this.apiService.get('/system/states_for_country', this.vendor.country_code).then(states => {
      this.states = states;
      return this.apiService.get('/system/country_validations', this.vendor.country_code);
    }).then(validations => {
      this.validations = validations;
    }).catch(err => this.messagesService.error(err));
  }

  syncWithHeadOffice () {
    Promise.resolve()
    .then(() => this.vendorObj.save())
    .then(() => this.apiService.patch('/vendors/sync_multibranch_vendor_entry', this.vendor))
    .then(() => {
      this.messagesService.show(this.translateService.instant('MULTI_BRANCH_SPACE.VENDOR_LINKED'), 'success');
      this.router.stateService.reload();
    })
    .catch(err => this.messagesService.error(err));
  }
}
